/**
 * The fire sequence's auto-selection ("Barrage fires from targeting mode", 2026-09-24): on entry
 * and after each shot, the command menu arms the first unfired weapon of the committed mounts, so
 * the player stays in targeting mode and clicks targets. Pure, so the host
 * (`TokenCommandMenuHost.tsx`) only schedules it.
 */
import type { CommandMenuFire } from "./commandMenu";
import type { ActionDraft } from "./combatInspector";

/**
 * A pending auto-arm. `exclude` is the weapon just fired, which the fire card still lists as
 * fireable until the server's lock echoes the shot; `firedLen` is the lock's fired count when it
 * was sent, so a longer list is that echo. No `exclude` on entry.
 */
export type PendingArm = { exclude?: string; firedLen: number };

/**
 * The weapon to arm next, or `null` to wait: the first fireable weapon, skipping the one just fired
 * until the lock has echoed it. `null` while the only fireable weapon is that one (the echo either
 * strikes it and the lock closes, or a rejection clears `exclude`).
 */
export function nextFireWeapon(fire: CommandMenuFire, pending: PendingArm): string | null {
  const echoed = fire.fired.length !== pending.firedLen;
  return fire.fireable.find((k) => echoed || k !== pending.exclude) ?? null;
}

/**
 * The aim the board's washes read: the armed aim, else the hovered preview, with two exceptions
 * where the preview wins until the hover ends:
 * - a preview of another weapon of the armed shot (the same unit's Skirmish or Barrage: a row
 *   hovered in the fire sequence's list, a card in the fixed-target Skirmish chooser);
 * - any preview while a Prepare is armed (range-hover): a hold picks nothing on the board and paints
 *   nothing, so its card's weapons and the menu's rows show their reach as they would unarmed.
 * Everywhere else the armed aim keeps the board, because the target walls that route a board click
 * read this same aim (`outOfRangeTargetIds`).
 */
export function washAimFor(aim: ActionDraft | null, previewAim: ActionDraft | null): ActionDraft | null {
  if (aim && previewAim && (aim.mode === "skirmish" || aim.mode === "barrage") && previewAim.mode === aim.mode && previewAim.attackerId === aim.attackerId) return previewAim;
  if (aim?.mode === "prepare" && previewAim) return previewAim;
  return aim ?? previewAim;
}
